/**
 * Basemap style construction — dual mode.
 *
 * PRIMARY (offline): a self-contained vector basemap from a local `.pmtiles`
 * file, with locally-hosted glyphs, styled by `protomaps-themes-base`. Renders
 * fully in airplane mode.
 *
 * FALLBACK (online): OSM raster tiles. Used automatically when the `.pmtiles`
 * asset is absent (see `__HAS_PMTILES__`, injected at build time from whether
 * `public/basemap/region.pmtiles` exists) or if the vector source fails to
 * load at runtime. Basemap then needs network, but data/UI/markers and
 * last-viewed tiles still work offline. See docs/DECISIONS.md.
 */

import type { StyleSpecification } from 'maplibre-gl';
import layers from 'protomaps-themes-base';

/** Region bounding box from the brief (padded to cover every stop). */
export const REGION_BBOX = {
  west: 19.35,
  south: 49.2,
  east: 20.55,
  north: 50.15,
} as const;

const BASE = import.meta.env.BASE_URL; // e.g. "/trip-planner/"

export const PMTILES_URL = `${BASE}basemap/region.pmtiles`;
const GLYPHS_URL = `${BASE}basemap/glyphs/{fontstack}/{range}.pbf`;

const OSM_ATTRIBUTION =
  '© <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors';

/** Whether the offline vector basemap asset was bundled at build time. */
export const hasOfflineBasemap: boolean =
  typeof __HAS_PMTILES__ !== 'undefined' && __HAS_PMTILES__;

/** Vector style backed by the local PMTiles file + local glyphs. */
export function vectorStyle(): StyleSpecification {
  return {
    version: 8,
    glyphs: GLYPHS_URL,
    sources: {
      protomaps: {
        type: 'vector',
        url: `pmtiles://${PMTILES_URL}`,
        attribution: `${OSM_ATTRIBUTION} · <a href="https://protomaps.com" target="_blank" rel="noopener">Protomaps</a>`,
      },
    },
    // `light` theme, English labels (keeps place names in Latin script).
    layers: layers('protomaps', 'light', 'en'),
  };
}

/** Online raster fallback style (OpenStreetMap tiles). */
export function rasterStyle(): StyleSpecification {
  return {
    version: 8,
    glyphs: GLYPHS_URL,
    sources: {
      osm: {
        type: 'raster',
        tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
        tileSize: 256,
        maxzoom: 19,
        attribution: OSM_ATTRIBUTION,
      },
    },
    layers: [{ id: 'osm', type: 'raster', source: 'osm' }],
  };
}

/** The style to use on first load, per the build-time basemap decision. */
export function initialStyle(): StyleSpecification {
  return hasOfflineBasemap ? vectorStyle() : rasterStyle();
}
